import type { Config } from "../types";
import { withRequestDeadline } from "./network";

// Ported from app.html loadConfig()/canLive()/canPasskey(). One behavioral change
// vs the original: fetch "/config.json" (root-absolute) instead of "./config.json"
// so the single Terraform-generated object serves both the /next/ preview and the
// post-cutover root. Cognito values are NEVER read from import.meta.env - config
// is runtime-only, so infra stays the single source of truth.
let CFG: Partial<Config> = {};
let loaded = false;

export async function loadConfig(): Promise<Partial<Config>> {
  if (loaded) return CFG;
  // A failed load must remain retryable. An empty cached config used to send a
  // disconnected visitor into the demo sign-in path for the rest of this tab.
  CFG = await withRequestDeadline(async (signal) => {
    const response = await fetch("/config.json", { cache: "no-store", signal });
    if (!response.ok) throw new Error("Couldn't load Life OS settings. Try again.");
    const config = await response.json() as Partial<Config> | null;
    if (!config || !config.apiBase || !config.cognitoDomain || !config.cognitoClientId)
      throw new Error("Life OS settings are unavailable. Try again.");
    return config;
  }, 15_000);
  loaded = true;
  return CFG;
}

export const getCfg = (): Partial<Config> => CFG;

export const canLive = (): boolean =>
  !!(CFG.apiBase && CFG.cognitoDomain && CFG.cognitoClientId);

// Passkeys only in a secure context with WebAuthn support AND when this origin's
// host matches the pool's relying-party id - so the passkey UI stays hidden on
// the raw CloudFront URL / localhost (where WebAuthn would SecurityError on rpId).
export const pkSupported = (): boolean =>
  !!(window.PublicKeyCredential && window.isSecureContext);

export const canPasskey = (): boolean =>
  pkSupported() &&
  !!(CFG.cognitoClientId && CFG.cognitoDomain && CFG.relyingPartyId) &&
  (location.hostname === CFG.relyingPartyId ||
    location.hostname.endsWith("." + CFG.relyingPartyId));
